import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormArray, AbstractControl, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { ApiCallService } from "../../../../api/service/api-call.service";
import * as Constants from "../../../../constant/Constants";
import { IConfirmDialogData } from "src/app/components/dialog/dialog.data";
import { ConfirmComponent } from "src/app/components/dialog/confirm/confirm.component";
import { MatDialog } from "@angular/material/dialog";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-dp-imei-details",
  templateUrl: "./imei-details.component.html",
  styleUrls: ["./imei-details.component.css"],
})
export class DpImeiDetailsComponent implements OnInit {
  token: string = "placeholder-value";
  loading: boolean = false;
  url: string = environment.baseUrl.replace("/motor","") + "/dp-chat"  + Constants.dpImeiDetailsURL;

  genericDialogData: IConfirmDialogData = {
    title: "",
    content: "",
    cancelButtonText: "",
    confirmButtonText: "",
  };

  dpForm = this._formBuilder.group({
    imeiNumbers: this._formBuilder.array([
      this._formBuilder.control("", [Validators.required, Validators.pattern(/^\d{15}$/)]),
    ]),
  });

  constructor(
    private route: ActivatedRoute,
    private apiCallService: ApiCallService,
    private _formBuilder: FormBuilder,
    public dialog: MatDialog
  ) {}

  get imeiNumbers(): FormArray {
    return this.dpForm.get("imeiNumbers") as FormArray;
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      if (params["token"] && params["token"] !== "undefined") {
        this.token = params["token"];
        this.loadPrefill();
      }
    });

    this.subscribeImeiStripping(this.imeiNumbers.at(0));
  }

  async loadPrefill(): Promise<void> {
    try {
      const response = await this.apiCallService.getRequest(`${this.url}?token=${this.token}`);
      if (response && response.error === false && response.prefill) {
        const saved: string[] = response.prefill.imeiNumbers;
        if (Array.isArray(saved) && saved.length > 0) {
          // Rebuild the FormArray to match the saved count
          while (this.imeiNumbers.length > 0) {
            this.imeiNumbers.removeAt(0);
          }
          saved.forEach((imei) => {
            const ctrl = this._formBuilder.control(imei || "", [
              Validators.required,
              Validators.pattern(/^\d{15}$/),
            ]);
            this.subscribeImeiStripping(ctrl);
            this.imeiNumbers.push(ctrl);
          });
        }
      }
    } catch (error) {
      console.error("Failed to load prefill data", error);
    }
  }

  private subscribeImeiStripping(ctrl: AbstractControl): void {
    ctrl.valueChanges.subscribe((value) => {
      if (value) {
        const raw = value.replace(/\D/g, "");
        if (raw !== value) {
          ctrl.setValue(raw, { emitEvent: false });
        }
      }
    });
  }

  addImei(): void {
    const ctrl = this._formBuilder.control("", [
      Validators.required,
      Validators.pattern(/^\d{15}$/),
    ]);
    this.subscribeImeiStripping(ctrl);
    this.imeiNumbers.push(ctrl);
  }

  removeImei(index: number): void {
    if (this.imeiNumbers.length > 1) {
      this.imeiNumbers.removeAt(index);
    }
  }

  getImeiError(index: number): string {
    const ctrl = this.imeiNumbers.at(index);
    if (ctrl.touched && ctrl.errors) {
      if (ctrl.errors["required"]) return "IMEI number is required";
      if (ctrl.errors["pattern"]) return "IMEI must be exactly 15 digits";
    }
    return "";
  }

  submitForm(): void {
    this.imeiNumbers.controls.forEach((c) => c.markAsTouched());
    if (!this.dpForm.valid) return;

    this.initDialogData(
      "Confirm submission",
      "Please proceed only if you have verified that the IMEI details are correct",
      "Back",
      "Proceed"
    );
    this.showGenericPopup(this.postDataToWhatsapp);
  }

  postDataToWhatsapp = async () => {
    this.loading = true;

    const req = {
      imeiNumbers: this.imeiNumbers.value,
      token: this.token,
      filled: true,
    };

    try {
      const response = await this.apiCallService.postRequest(
        `${this.url}?token=${this.token}`,
        req
      );
      if (response.error === false) {
        window.location.href = response.nextUrl ?? environment.whatsappRedirectURL;
      } else {
        this.loading = false;
        this.initDialogData(
          "Submission Failed",
          "An error occurred while saving the form details",
          "Cancel",
          "TRY AGAIN"
        );
        this.showGenericPopup(this.postDataToWhatsapp);
      }
    } catch (error) {
      console.error(error);
      this.loading = false;
      this.initDialogData(
        "Submission Failed",
        "An unexpected error occurred",
        "Cancel",
        "TRY AGAIN"
      );
      this.showGenericPopup(this.postDataToWhatsapp);
    }
  };

  confirmCancel(): void {
    this.initDialogData(
      "Warning",
      "Are you sure you want to cancel the application?",
      "back",
      "Cancel application"
    );
    this.showGenericPopup(this.cancelApplication);
  }

  cancelApplication = async () => {
    this.loading = true;
    const req = { token: this.token, filled: false };
    try {
      const response = await this.apiCallService.postRequest(
        `${this.url}?token=${this.token}`,
        req
      );
      if (response.error === false) {
        window.location.href = environment.whatsappRedirectURL;
      } else {
        this.loading = false;
        this.initDialogData(
          "Failed",
          "An error occurred while attempting to cancel",
          "Cancel",
          "TRY AGAIN"
        );
        this.showGenericPopup(this.cancelApplication);
      }
    } catch (error) {
      console.error(error);
      this.loading = false;
      window.location.href = environment.whatsappRedirectURL;
    }
  };

  showGenericPopup(runFunction: () => void): void {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      disableClose: true,
      data: this.genericDialogData,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        runFunction();
      }
    });
  }

  initDialogData(
    title: string,
    content: string,
    cancelButtonText: string,
    confirmButtonText: string
  ): void {
    this.genericDialogData.title = title;
    this.genericDialogData.content = content;
    this.genericDialogData.cancelButtonText = cancelButtonText;
    this.genericDialogData.confirmButtonText = confirmButtonText;
  }
}
